import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Play, Pause, Volume2, VolumeX, Maximize, X, 
  Heart, ChevronRight, Sparkles, Video, BarChart3
} from "lucide-react";

// All available feature videos with detailed info - Extended versions
const FEATURE_VIDEOS = {
  promo: {
    src: "/videos/goodwin-cares-promo-extended.mp4",
    title: "Goodwin Cares Overview",
    duration: "0:12",
    description: "See how we combine AI-powered intelligence with human compassion"
  },
  telehealth: {
    src: "/videos/feature-telehealth-extended.mp4",
    title: "Telehealth Video Calls",
    duration: "0:12",
    description: "HIPAA-compliant video consultations with patients and families"
  },
  scheduling: {
    src: "/videos/feature-scheduling-extended.mp4", 
    title: "Smart Scheduling",
    duration: "0:12",
    description: "AI-optimized visit planning with geographic clustering"
  },
  training: {
    src: "/videos/feature-training-extended.mp4",
    title: "Hospice Academy",
    duration: "0:12", 
    description: "ELNEC-aligned training with CEU certificates"
  },
  patientCare: {
    src: "/videos/feature-patient-care-extended.mp4",
    title: "Patient Care Dashboard",
    duration: "0:12",
    description: "Track symptoms, medications, and health trends"
  },
  family: {
    src: "/videos/feature-family-extended.mp4",
    title: "Family Connection",
    duration: "0:12",
    description: "Keep families connected during the hospice journey"
  },
  analytics: {
    src: "/videos/feature-analytics-extended.mp4",
    title: "Analytics Dashboard",
    duration: "0:12",
    description: "Real-time operations and predictive insights"
  },
  overview: {
    src: "/videos/feature-complete-overview.mp4",
    title: "Complete Platform",
    duration: "0:12",
    description: "See all features working together"
  }
};

/**
 * Enhanced VideoShowcase - Main promo video with feature video carousel
 */
export default function VideoShowcase({ 
  src = "/videos/goodwin-cares-promo-extended.mp4",
  title = "Goodwin Cares",
  subtitle = "Compassionate Care, Powered by Technology",
  showFeatureVideos = true
}) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [selectedFeatureVideo, setSelectedFeatureVideo] = useState(null);
  const [hasError, setHasError] = useState(false);
  const controlsTimeoutRef = useRef(null);

  // Video control functions
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const percentage = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(percentage);
    }
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    setProgress(0);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    }
  };

  const openFullscreen = () => {
    setShowFullscreen(true);
  };

  const openFeatureVideo = (videoKey) => {
    setSelectedFeatureVideo(FEATURE_VIDEOS[videoKey]);
  };

  // Feature videos to show (excluding the main promo)
  const featureVideoList = Object.entries(FEATURE_VIDEOS)
    .filter(([key]) => key !== 'promo')
    .map(([key, video]) => ({ key, ...video }));

  const retryVideo = () => {
    setHasError(false);
    if (videoRef.current) {
      videoRef.current.load();
    }
  };

  return (
    <div className="space-y-8">
      {/* Main Video Player */}
      <div 
        className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900 group"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => isPlaying && setShowControls(false)}
      >
        {/* Video Element */}
        <div className="aspect-video relative">
          {hasError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-teal-900 to-gray-900 text-white">
              <Heart className="w-16 h-16 mb-4 text-primary animate-pulse" />
              <h3 className="text-xl font-medium mb-2">{title}</h3>
              <p className="text-white/70 text-center max-w-md px-4">{subtitle}</p>
              <Badge className="mt-4 bg-primary cursor-pointer hover:bg-primary/80" onClick={retryVideo}>
                Click to Play Video
              </Badge>
            </div>
          ) : (
            <video
              ref={videoRef}
              src={src}
              className="w-full h-full object-cover"
              muted={isMuted}
              playsInline
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleVideoEnd}
              onError={() => setHasError(true)}
              poster="/images/video-poster.jpg"
            />
          )}

          {/* Overlay Gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />

          {/* Title Overlay */}
          <div className="absolute top-6 left-6 right-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">{title}</h3>
                <p className="text-white/70 text-sm">{subtitle}</p>
              </div>
            </div>
          </div>

          {/* Play Button Overlay */}
          {!isPlaying && !hasError && (
            <button
              onClick={togglePlay}
              className="absolute inset-0 flex items-center justify-center group/play"
              aria-label="Play video"
            >
              <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center shadow-2xl transform group-hover/play:scale-110 transition-transform">
                <Play className="w-8 h-8 text-primary ml-1" />
              </div>
            </button>
          )}

          {/* Controls Bar */}
          {!hasError && (
            <div 
              className={`absolute bottom-0 left-0 right-0 p-4 transition-opacity duration-300 ${
                showControls ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {/* Progress Bar */}
              <div className="w-full h-1 bg-white/20 rounded-full mb-3 cursor-pointer overflow-hidden">
                <div 
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {/* Control Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-white hover:bg-white/20 h-9 w-9"
                    onClick={togglePlay}
                  >
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-white hover:bg-white/20 h-9 w-9"
                    onClick={toggleMute}
                  >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-white/20 text-white border-0">
                    {FEATURE_VIDEOS.promo.duration}
                  </Badge>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-white hover:bg-white/20 h-9 w-9"
                    onClick={openFullscreen}
                  >
                    <Maximize className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Feature Videos Carousel */}
      {showFeatureVideos && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <h3 className="font-medium text-foreground">Explore Features</h3>
            </div>
            <Link to="/video-library">
              <Button variant="ghost" size="sm" className="text-primary">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {featureVideoList.map((video) => (
              <Card 
                key={video.key}
                className="overflow-hidden cursor-pointer group hover:shadow-lg transition-all hover:-translate-y-1"
                onClick={() => openFeatureVideo(video.key)}
              >
                <div className="aspect-video relative bg-gradient-to-br from-teal-800 to-gray-900">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-5 h-5 text-white ml-0.5" />
                    </div>
                  </div>
                  <Badge className="absolute bottom-1 right-1 bg-black/60 text-xs">
                    {video.duration}
                  </Badge>
                </div>
                <CardContent className="p-2">
                  <p className="text-xs font-medium truncate">{video.title}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Fullscreen Dialog */}
      <Dialog open={showFullscreen} onOpenChange={setShowFullscreen}>
        <DialogContent className="max-w-5xl p-0 bg-black">
          <div className="aspect-video">
            <video
              src={src}
              className="w-full h-full"
              controls
              autoPlay
              onError={() => setHasError(true)}
            />
          </div>
          <Button
            size="icon"
            variant="ghost"
            className="absolute top-2 right-2 text-white hover:bg-white/20"
            onClick={() => setShowFullscreen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </DialogContent>
      </Dialog>

      {/* Feature Video Dialog */}
      <Dialog open={!!selectedFeatureVideo} onOpenChange={() => setSelectedFeatureVideo(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-primary" />
              {selectedFeatureVideo?.title}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">{selectedFeatureVideo?.description}</p>
          </DialogHeader>
          <div className="aspect-video bg-black">
            {selectedFeatureVideo && (
              <video
                src={selectedFeatureVideo.src}
                className="w-full h-full"
                controls
                autoPlay
                onError={(e) => {
                  e.target.parentElement.innerHTML = `
                    <div class="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-teal-900 to-gray-900 text-white">
                      <div class="text-4xl mb-4">🎬</div>
                      <h3 class="text-lg font-medium">${selectedFeatureVideo.title}</h3>
                      <p class="text-white/70 text-sm mt-2">Video coming soon</p>
                    </div>
                  `;
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
